// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { DateValidationError } from '@mui/x-date-pickers';
import { clientLogger } from '@seedplanner/logger';
import {
  MAX_TASK_DAILY_FREQUENCY,
  MAX_TASK_WEEKLY_FREQUENCY,
  TASK_STATUS,
  type TaskStatusType,
  dayOfWeekToNumber,
  numberToDayOfWeek,
  utils,
} from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '../../lib/base';
import { DASHBOARD_OVERDUE_TASKS_QUERY, DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import { RECURRING_TASKS_QUERY } from '../../queries/RecurringTaskQueries';
import type {
  CreateRecurringTaskMutation,
  CreateRecurringTaskMutationVariables,
  CreateTaskMutation,
  CreateTaskMutationVariables,
  EditRecurringTaskMutation,
  EditRecurringTaskMutationVariables,
  TaskFormRecurringTaskDataQuery,
  TaskFormRecurringTaskDataQueryVariables,
  TaskFormTaskDataQuery,
  TaskFormTaskDataQueryVariables,
} from '../../types/__generated__/graphql';
import { createID, getID, getTimeRanges } from '../../utils';
import { evictCacheData } from '../../utils/evictCacheData';
import { ExitDialogButton } from '../common/button';
import InternalDatePicker from '../common/date/InternalDatePicker';
import { ErrorView } from '../common/error';
import {
  DEFAULT_FREQUENCY_UNIT,
  EVERY_NTH_WEEKDAY_OF_MONTH,
  EVERY_X_DAYS,
  EVERY_X_DAY_OF_WEEK,
  type FrequencyLabel,
  daysOfTheWeek,
  frequencyLabelToInput,
  setFrequencyStates,
  taskFrequencyOptions,
} from '../common/frequency';
import { PageLoading } from '../common/loading';

const CREATE_TASK_MUTATION = gql`
  mutation CreateTask($task_name: String!, $task_status: String!, $task_time: Int!, $notes: String) {
    createTask(input: { task_name: $task_name, task_status: $task_status, task_time: $task_time, notes: $notes }) {
      task_created {
        id
        task_status
        updated_at
        task_completed_time
      }
    }
  }
`;
const CREATE_RECURRING_TASK_MUTATION = gql`
  mutation CreateRecurringTask(
    $task_name: String!
    $notes: String
    $recurring_start_time: Int!
    $recurring_end_time: Int
    $frequency_options: RecurringOrderOptionsInput!
  ) {
    createRecurringTask(
      input: {
        task_name: $task_name
        notes: $notes
        recurring_start_time: $recurring_start_time
        recurring_end_time: $recurring_end_time
        frequency_options: $frequency_options
      }
    ) {
      recurring_task_created {
        id
        task_name
        notes
        recurring_start_time
        recurring_end_time
        recurring_options {
          exceptions
          frequencyGroups {
            frequencyType
            frequencies
          }
        }
        updated_at
      }
      tasks_created {
        id
      }
    }
  }
`;
const EDIT_RECURRING_TASK_MUTATION = gql`
  mutation EditRecurringTask(
    $recurring_task_id: Int!
    $task_name: String!
    $notes: String
    $recurring_start_time: Int!
    $recurring_end_time: Int
    $frequency_options: RecurringOrderOptionsInput!
  ) {
    editRecurringTask(
      input: {
        recurring_task_id: $recurring_task_id
        task_name: $task_name
        notes: $notes
        recurring_start_time: $recurring_start_time
        recurring_end_time: $recurring_end_time
        frequency_options: $frequency_options
      }
    ) {
      updated_recurring_task {
        id
        task_name
        notes
        recurring_start_time
        recurring_end_time
        recurring_options {
          exceptions
          frequencyGroups {
            frequencyType
            frequencies
          }
        }
        updated_at
      }
      updated_tasks {
        id
        task_name
        notes
        updated_at
      }
      deleted_tasks {
        id
      }
      tasks_created {
        id
      }
    }
  }
`;
const TASK_QUERY = gql`
  query TaskFormTaskData($id: ID!) {
    task(id: $id) {
      id
      task_name
      task_status
      task_time
      notes
    }
  }
`;
const RECURRING_TASK_QUERY = gql`
  query TaskFormRecurringTaskData($id: ID!) {
    recurring_task(id: $id) {
      id
      task_name
      notes
      recurring_start_time
      recurring_end_time
      recurring_options {
        exceptions
        frequencyGroups {
          frequencyType
          frequencies
        }
      }
    }
  }
`;

interface Props {
  readonly taskId?: number;
  readonly recurringTaskId?: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly formIsForClone?: boolean;
  readonly isForNewSingleTask: boolean;
}

export default function EditAddTaskDialog({
  isOpen,
  onClose,
  onSuccess,
  formIsForClone,
  taskId,
  recurringTaskId,
  isForNewSingleTask,
}: Props) {
  const { timezone } = useUserContext();
  const apolloClient = useApolloClient();
  const [createRecurringTask, { loading: createRecurringTaskLoading }] = useMutation<
    CreateRecurringTaskMutation,
    CreateRecurringTaskMutationVariables
  >(CREATE_RECURRING_TASK_MUTATION);
  const [addTask, { loading: addTaskLoading, error: addTaskError }] = useMutation<
    CreateTaskMutation,
    CreateTaskMutationVariables
  >(CREATE_TASK_MUTATION);
  const [editRecurringTask, { loading: editRecurringTaskLoading }] = useMutation<
    EditRecurringTaskMutation,
    EditRecurringTaskMutationVariables
  >(EDIT_RECURRING_TASK_MUTATION);
  const [
    getRecurringTaskData,
    { data: recurringTaskData, error: recurringTaskDataError, loading: recurringTaskDataLoading },
  ] = useLazyQuery<TaskFormRecurringTaskDataQuery, TaskFormRecurringTaskDataQueryVariables>(RECURRING_TASK_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const [getTaskData, { data: taskData, error: taskDataError, loading: taskDataLoading }] = useLazyQuery<
    TaskFormTaskDataQuery,
    TaskFormTaskDataQueryVariables
  >(TASK_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  const { todayStart, sevenDaysFromTodayStart } = getTimeRanges(timezone);

  const isForRecurringTask = recurringTaskId !== undefined;
  const isForRegularTask = taskId !== undefined;
  const formIsForCloneTask = formIsForClone && (isForRecurringTask || isForRegularTask);
  const formIsForNewTask = !isForRecurringTask && !isForRegularTask;
  const isForEditRecurringTask = !formIsForCloneTask && isForRecurringTask;

  React.useEffect(() => {
    if (isOpen) {
      if (isForRegularTask) {
        void getTaskData({
          variables: {
            id: createID.task(taskId),
          },
          onCompleted: (dataIn) => {
            if (dataIn?.task != null) {
              const taskDataIn = dataIn.task;
              setTaskName(taskDataIn.task_name);
              setTaskStatus(taskDataIn.task_status as TaskStatusType);
              setNotes(taskDataIn.notes ?? '');
              setTaskTime(dayjs.unix(taskDataIn.task_time).tz(timezone));
              setIsRecurring(false);
              setRecursForever(false);
            } else {
              enqueueSnackbar('Something went wrong! No task data returned', {
                variant: 'error',
                autoHideDuration: 10000,
              });
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(`Error getting task data: ${errorIn.message}`, {
              variant: 'error',
              autoHideDuration: 10000,
            });
          },
        });
      } else if (isForRecurringTask) {
        void getRecurringTaskData({
          variables: {
            id: createID.recurringTask(recurringTaskId),
          },
          onCompleted: (dataIn) => {
            if (dataIn?.recurring_task != null) {
              const taskDataIn = dataIn.recurring_task;
              setTaskName(taskDataIn.task_name);
              setNotes(taskDataIn.notes ?? '');
              setIsRecurring(true);

              setFrequencyStates({
                timezone,
                recurringOptions: taskDataIn.recurring_options,
                startTime: taskDataIn.recurring_start_time,
                endTime: taskDataIn.recurring_end_time,
                setFrequencyUnit,
                setFrequency,
                setDayOfWeek,
                setOccurence,
                setRecursForever,
                setStartDate,
                setEndDate,
              });
            } else {
              enqueueSnackbar('Something went wrong! No recurring task data returned', {
                variant: 'error',
              });
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(`Error getting recurring task data: ${errorIn.message}`, {
              variant: 'error',
              autoHideDuration: 10000,
            });
          },
        });
      }
    }
  }, [
    isOpen,
    getRecurringTaskData,
    recurringTaskId,
    timezone,
    getTaskData,
    isForRegularTask,
    isForRecurringTask,
    taskId,
  ]);

  const [taskName, setTaskName] = React.useState('');
  const [taskNameValidation, setTaskNameValidation] = React.useState('');
  const onTaskNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTaskName(e.target.value);
    if (e.target.value !== '') {
      setTaskNameValidation('');
    }
  };
  const onTaskNameBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.value === '') {
      setTaskNameValidation('Task name is required');
    }
  };
  const [taskStatus, setTaskStatus] = React.useState<TaskStatusType>(TASK_STATUS.PLANNED);
  const onTaskStatusChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTaskStatus(e.target.value as TaskStatusType);
  };
  const [notes, setNotes] = React.useState('');
  const onNotesChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  };
  const [taskTime, setTaskTime] = React.useState<dayjs.Dayjs | null>(null);
  const [isRecurring, setIsRecurring] = React.useState(!isForNewSingleTask);
  const [recursForever, setRecursForever] = React.useState(!isForNewSingleTask);
  const [startDate, setStartDate] = React.useState<dayjs.Dayjs | null>(utils.todayStart());
  const [endDate, setEndDate] = React.useState<dayjs.Dayjs | null>(null);
  const [startDateError, setStartDateError] = React.useState<DateValidationError | null>(null);
  // Frequency for every X weeks
  const [frequency, setFrequency] = React.useState('1');
  // Frequency type: every X weeks, every X day of the week, every nth weekday of the month
  const [frequencyUnit, setFrequencyUnit] = React.useState(DEFAULT_FREQUENCY_UNIT);
  // Day of the week for every nth weekday of the month or for every X day of the week
  const [dayOfWeek, setDayOfWeek] = React.useState(numberToDayOfWeek(dayjs().day()));
  // Occurence for every nth weekday of the month
  const [occurence, setOccurence] = React.useState('1');

  const startDateErrorMessage = React.useMemo(() => {
    if (startDateError == null) {
      return '';
    }
    switch (startDateError) {
      case 'maxDate':
        return 'Start date cannot be after end date';
      case 'minDate':
        return 'Start date cannot be before today';
      default:
        return '';
    }
  }, [startDateError]);

  const onChangeIsRecurring = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsRecurring(e.target.checked);
  };
  const onChangeRecursForever = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRecursForever(e.target.checked);
  };
  const onChangeStartDate = (newValue: dayjs.Dayjs | null) => {
    setStartDate(newValue);
  };
  const onChangeEndDate = (newValue: dayjs.Dayjs | null) => {
    setEndDate(newValue);
  };
  const onFrequencyUnitChange = (e: SelectChangeEvent<string>) => {
    setFrequencyUnit(e.target.value);
  };
  const onDayOfWeekChange = (e: SelectChangeEvent<string>) => {
    setDayOfWeek(e.target.value);
  };
  const onChangeFrequencySelect = (e: SelectChangeEvent<string>) => {
    setFrequency(e.target.value);
  };
  const onChangeOccurence = (e: SelectChangeEvent<string>) => {
    setOccurence(e.target.value);
  };
  dayjs.extend(utc);
  const frequencyOptionsForSubmit = {
    exceptions: [],
    frequency_groups: [
      {
        frequency_type: frequencyLabelToInput(frequencyUnit as FrequencyLabel),
        frequencies:
          frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH
            ? [dayOfWeekToNumber(dayOfWeek || 'mon'), Number.parseInt(occurence || '0')]
            : [
                frequencyUnit === EVERY_X_DAY_OF_WEEK
                  ? dayOfWeekToNumber(dayOfWeek || 'mon')
                  : Number.parseInt(frequency || '0'),
              ],
      },
    ],
  };

  const onSubmit = () => {
    if (isForEditRecurringTask) {
      void editRecurringTask({
        refetchQueries: [
          {
            query: DASHBOARD_QUERY,
            variables: {
              todayStart: todayStart.toString(),
              sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
            },
          },
          {
            query: DASHBOARD_OVERDUE_TASKS_QUERY,
            variables: {
              todayStart: todayStart.toString(),
              first: 25,
            },
          },
        ],
        variables: {
          recurring_task_id: recurringTaskId,
          task_name: taskName,
          notes: notes === '' ? null : notes,
          recurring_start_time: utils.toNoonTime(startDate ?? utils.todayStart(), timezone).unix(),
          recurring_end_time: endDate == null ? null : utils.toNoonTime(endDate, timezone).unix(),
          frequency_options: frequencyOptionsForSubmit,
        },
        update: (cache, { data }) => {
          const tasksCreated = (data?.editRecurringTask?.tasks_created?.length ?? 0) > 0;
          const tasksDeleted = (data?.editRecurringTask?.deleted_tasks?.length ?? 0) > 0;
          cache.modify({
            fields: {
              task_groups(existingGroup, { DELETE }) {
                return tasksCreated || tasksDeleted ? DELETE : existingGroup;
              },
              tasks(existingTasks, { DELETE }) {
                return tasksCreated || tasksDeleted ? DELETE : existingTasks;
              },
            },
          });
        },
        onCompleted: (dataIn) => {
          if (dataIn?.editRecurringTask != null) {
            enqueueSnackbar(`Edited Recurring Task ${getID(dataIn.editRecurringTask.updated_recurring_task.id)}`, {
              variant: 'success',
            });
            evictCacheData({
              apolloClient,
              taskIds: dataIn.editRecurringTask.deleted_tasks.map((t) => t.id),
            });
            setTaskName('');
            setNotes('');
            setTaskStatus(TASK_STATUS.PLANNED);
            setStartDate(utils.todayStart());
            setEndDate(null);
            setFrequencyUnit(DEFAULT_FREQUENCY_UNIT);
            setIsRecurring(true);
            setRecursForever(true);
            setFrequency('');
            setOccurence('1');
            setDayOfWeek('');
            onSuccess();
          } else {
            enqueueSnackbar('Error editing recurring task', { variant: 'error', autoHideDuration: 10000 });
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    } else {
      if (isRecurring) {
        void createRecurringTask({
          refetchQueries: [
            { query: RECURRING_TASKS_QUERY },
            {
              query: DASHBOARD_QUERY,
              variables: {
                todayStart: todayStart.toString(),
                sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
              },
            },
            {
              query: DASHBOARD_OVERDUE_TASKS_QUERY,
              variables: {
                todayStart: todayStart.toString(),
                first: 25,
              },
            },
          ],
          update: (cache, { data }) => {
            const tasksCreated = (data?.createRecurringTask?.tasks_created?.length ?? 0) > 0;
            cache.modify({
              fields: {
                task_groups(existingGroup, { DELETE }) {
                  return tasksCreated ? DELETE : existingGroup;
                },
                tasks(existingTasks, { DELETE }) {
                  return tasksCreated ? DELETE : existingTasks;
                },
              },
            });
          },
          variables: {
            task_name: taskName,
            notes: notes === '' ? null : notes,
            recurring_start_time: utils.toNoonTime(startDate ?? utils.todayStart(), timezone).unix(),
            recurring_end_time: endDate == null ? null : utils.toNoonTime(endDate, timezone).unix(),
            frequency_options: frequencyOptionsForSubmit,
          },
          onCompleted: (data) => {
            if (data !== undefined && data.createRecurringTask != null) {
              const taskDataIn = data.createRecurringTask;
              enqueueSnackbar(`Created Recurring Task ${getID(taskDataIn.recurring_task_created.id)}`, {
                variant: 'success',
              });
              setTaskName('');
              setNotes('');
              setTaskStatus(TASK_STATUS.PLANNED);
              setStartDate(utils.todayStart());
              setEndDate(null);
              setFrequencyUnit(DEFAULT_FREQUENCY_UNIT);
              setIsRecurring(true);
              setRecursForever(true);
              setFrequency('');
              setOccurence('1');
              setDayOfWeek('');
              onSuccess();
            } else {
              enqueueSnackbar(`Error creating recurring task: ${addTaskError?.message ?? 'Unknown'}`, {
                variant: 'error',
              });
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(`Error creating recurring task: ${errorIn.message}`, {
              variant: 'error',
              autoHideDuration: 10000,
            });
            clientLogger.error({ title: 'new_task_error', message: errorIn.message });
          },
        });
      } else {
        void addTask({
          refetchQueries: [
            {
              query: DASHBOARD_QUERY,
              variables: {
                todayStart: todayStart.toString(),
                sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
              },
            },
            {
              query: DASHBOARD_OVERDUE_TASKS_QUERY,
              variables: {
                todayStart: todayStart.toString(),
                first: 25,
              },
            },
          ],
          update: (cache) => {
            cache.modify({
              fields: {
                task_groups(_, { DELETE }) {
                  return DELETE;
                },
                tasks(_, { DELETE }) {
                  return DELETE;
                },
              },
            });
          },
          variables: {
            task_name: taskName,
            task_status: taskStatus,
            task_time: taskTime == null ? -1 : utils.toNoonTime(taskTime, timezone).unix(),
            notes: notes === '' ? null : notes,
          },
          onCompleted: (data) => {
            if (data !== undefined && data.createTask != null) {
              const taskDataIn = data.createTask;
              enqueueSnackbar(`Created Task ${getID(taskDataIn.task_created.id)}`, { variant: 'success' });
              setTaskName('');
              setNotes('');
              setTaskStatus(TASK_STATUS.PLANNED);
              setStartDate(utils.todayStart(timezone));
              setEndDate(null);
              setFrequencyUnit(DEFAULT_FREQUENCY_UNIT);
              setIsRecurring(true);
              setRecursForever(true);
              setFrequency('');
              setOccurence('1');
              setDayOfWeek('');
              onSuccess();
            } else {
              enqueueSnackbar(`Error creating task: ${addTaskError?.message ?? 'Unknown'}`, {
                variant: 'error',
                autoHideDuration: 10000,
              });
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
            clientLogger.error({ title: 'new_task_error', message: errorIn.message });
          },
        });
      }
    }
  };

  const hasInputValidationError = startDateErrorMessage !== '' || taskNameValidation !== '';
  const requiredFieldsFilled =
    (isRecurring ? startDate !== null : taskTime !== null) &&
    taskName !== '' &&
    (isRecurring
      ? (frequencyUnit === EVERY_X_DAY_OF_WEEK ? dayOfWeek !== '' : frequency !== '') &&
        (recursForever ? true : endDate !== null)
      : true);
  const disableFields =
    addTaskLoading ||
    createRecurringTaskLoading ||
    recurringTaskDataLoading ||
    taskDataLoading ||
    editRecurringTaskLoading;
  const isSubmitDisabled = !requiredFieldsFilled || hasInputValidationError || disableFields;

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <TextField
        aria-describedby="task-name"
        id="task-name"
        label="Task Name"
        value={taskName}
        onChange={onTaskNameChange}
        error={taskNameValidation !== ''}
        subtext={taskNameValidation}
        onBlur={onTaskNameBlur}
        // onEnter={onEnter}
        hasSubtext
        required
        disabled={disableFields}
      />
      <Box display="flex" flexDirection="row" flexWrap="nowrap" gap={2}>
        {isForEditRecurringTask ? null : (
          <Box display="flex" flexDirection="column" sx={{ pl: '14px' }}>
            <FormControl required={true}>
              <FormLabel id="order-is-recurring-label">Recurring Task</FormLabel>
              <Switch checked={isRecurring} onChange={onChangeIsRecurring} />
            </FormControl>
          </Box>
        )}
        {isRecurring && (
          <Box display="flex" flexDirection="column" sx={{ pl: '14px' }}>
            <FormControl required={true}>
              <FormLabel id="order-recurs-forever-label">Recur Forever</FormLabel>
              <Switch checked={recursForever} onChange={onChangeRecursForever} />
            </FormControl>
          </Box>
        )}
      </Box>
      {!isRecurring && (
        <Box display="flex" flexDirection="column" sx={{ pl: '14px' }}>
          <FormControl required={true}>
            <FormLabel id="task-status-group-label">Task Status</FormLabel>
            <RadioGroup aria-labelledby="task-status-group-label" value={taskStatus} onChange={onTaskStatusChange} row>
              <FormControlLabel value={TASK_STATUS.PLANNED} control={<Radio />} label="Planned" />
              <FormControlLabel value={TASK_STATUS.COMPLETED} control={<Radio />} label="Completed" />
            </RadioGroup>
          </FormControl>
        </Box>
      )}
      <InternalDatePicker
        label={isRecurring ? 'Start Date * ' : 'Task Date *'}
        value={isRecurring ? startDate : taskTime}
        onChange={isRecurring ? onChangeStartDate : setTaskTime}
        disabled={disableFields}
        // When an existing recurring task is being edited
        // We don't want to show an error if the start date is before today- it almost always will be
        minDate={isForEditRecurringTask || !isRecurring ? undefined : utils.todayStart(timezone)}
        maxDate={isRecurring && !recursForever ? (endDate ?? undefined) : undefined}
        onError={(newError) => setStartDateError(newError)}
        textFieldProps={{
          helperText: startDateErrorMessage,
        }}
      />
      {isRecurring && !recursForever && (
        <InternalDatePicker
          minDate={isRecurring ? (startDate ?? utils.todayStart(timezone)) : undefined}
          label="End Date *"
          value={endDate}
          onChange={onChangeEndDate}
          disabled={disableFields}
        />
      )}
      {isRecurring && (
        <>
          <Box>
            <FormControl fullWidth>
              <InputLabel id="frequency-unit-selector">Frequency Type</InputLabel>
              <Select
                aria-describedby="frequency-unit-selector"
                id="frequency-unit-selector"
                labelId="frequency-unit-selector"
                label="Frequency Type"
                value={frequencyUnit}
                onChange={onFrequencyUnitChange}
                // onEnter={onEnter}
                required={true}
                disabled={disableFields}
              >
                {taskFrequencyOptions.map((option) => {
                  return (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
          <Box>
            {frequencyUnit === EVERY_X_DAY_OF_WEEK ? (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Typography>Every</Typography>
                <FormControl fullWidth>
                  <InputLabel id="frequency-day-of-week-selector">Day of Week</InputLabel>
                  <Select
                    aria-describedby="frequency-day-of-week-selector"
                    id="frequency-day-of-week-selector"
                    labelId="frequency-day-of-week-selector"
                    label="Day of Week"
                    value={dayOfWeek}
                    onChange={onDayOfWeekChange}
                    // onEnter={onEnter}
                    required={true}
                    disabled={disableFields}
                  >
                    {daysOfTheWeek.map((day) => {
                      return (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            ) : frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH ? (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Typography>Every</Typography>
                <FormControl fullWidth>
                  <InputLabel id="frequency-nth-week-of-month-selector">Occurrence</InputLabel>
                  <Select
                    aria-describedby="frequency-nth-week-of-month-selector"
                    id="frequency-nth-week-of-month-selector"
                    labelId="frequency-nth-week-of-month-selector"
                    label="Occurence"
                    value={occurence}
                    onChange={onChangeOccurence}
                    // onEnter={onEnter}
                    required={true}
                    disabled={disableFields}
                  >
                    {[
                      { value: '0', label: '1st' },
                      { value: '1', label: '2nd' },
                      { value: '2', label: '3rd' },
                      { value: '3', label: '4th' },
                    ].map((occ) => {
                      return (
                        <MenuItem key={occ.value} value={occ.value}>
                          {occ.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="frequency-day-of-week-selector">Day of Week</InputLabel>
                  <Select
                    aria-describedby="frequency-day-of-week-selector"
                    id="frequency-day-of-week-selector"
                    labelId="frequency-day-of-week-selector"
                    label="Day of Week"
                    value={dayOfWeek}
                    onChange={onDayOfWeekChange}
                    // onEnter={onEnter}
                    required={true}
                    disabled={disableFields}
                  >
                    {daysOfTheWeek.map((day) => {
                      return (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Typography>of the month</Typography>
              </Box>
            ) : frequencyUnit === EVERY_X_DAYS ? (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Typography>Every</Typography>
                <FormControl fullWidth>
                  <InputLabel id="daily-recurring-task-frequency">Frequency</InputLabel>
                  <Select
                    aria-describedby="daily-recurring-task-frequency"
                    id="daily-recurring-task-frequency"
                    labelId="daily-recurring-task-frequency"
                    label="Frequency"
                    value={frequency}
                    onChange={onChangeFrequencySelect}
                    // onEnter={onEnter}
                    required={true}
                    disabled={disableFields}
                  >
                    {Array.from({ length: MAX_TASK_DAILY_FREQUENCY }, (_, i) => (i + 1).toString()).map((freq) => {
                      return (
                        <MenuItem key={freq} value={freq}>
                          {freq}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Typography>days</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Typography>Every</Typography>
                <FormControl fullWidth>
                  <InputLabel id="weekly-recurring-task-frequency">Frequency</InputLabel>
                  <Select
                    aria-describedby="weekly-recurring-task-frequency"
                    id="weekly-recurring-task-frequency"
                    labelId="weekly-recurring-task-frequency"
                    label="Frequency"
                    value={frequency}
                    onChange={onChangeFrequencySelect}
                    // onEnter={onEnter}
                    required={true}
                    disabled={disableFields}
                  >
                    {Array.from({ length: MAX_TASK_WEEKLY_FREQUENCY }, (_, i) => (i + 1).toString()).map((freq) => {
                      return (
                        <MenuItem key={freq} value={freq}>
                          {freq}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Typography>weeks</Typography>
              </Box>
            )}
          </Box>
        </>
      )}
      <TextField
        aria-describedby="task-notes"
        id="task-notes"
        label="Notes"
        value={notes}
        onChange={onNotesChange}
        // onEnter={onEnter}
        disabled={disableFields}
      />
    </Box>
  );

  let dialogContent = form;
  if (isForRecurringTask) {
    dialogContent = recurringTaskDataError ? (
      <ErrorView error={recurringTaskDataError} />
    ) : recurringTaskDataLoading || recurringTaskData?.recurring_task == null ? (
      <PageLoading />
    ) : (
      form
    );
  } else if (isForRegularTask) {
    dialogContent = taskDataError ? (
      <ErrorView error={taskDataError} />
    ) : taskDataLoading || taskData?.task == null ? (
      <PageLoading />
    ) : (
      form
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {formIsForNewTask || formIsForCloneTask ? 'New Task' : 'Edit Recurring Task'}
          </Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button sx={{ width: { xs: '100%', md: '50%' } }} variant="outlined" onClick={onClose} disabled={disableFields}>
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
